// External Libraries
import React, { useEffect, useState } from 'react';
import { MultiValue, SingleValue } from 'react-select';

// Assets
import SupportParnershipInfo from '../assets/questionMark.png';

// Styles
import '../styles/IdeasFilter.css';

// Redux/State Management
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

// API Services
import { getIdeasCount } from '../api/IdeaService';
import { getUsersHavingIdeas } from '../api/UserService';

// Types
import {
  CountResponse,
  FilterUsersResponse,
  IdeaFilterParams,
  Partnership,
} from '../types/types';

// Components
import Button from './common/Button';
import Label from './common/Label';
import CustomSelect from './common/Select';
import Text from './common/Text';
import Loading from './common/Loading';

interface IdeasFilterProps {
  toggleFilter: (isVisible: boolean) => void;
  filterIdeas: (ideasFilter: IdeaFilterParams) => void;
}

interface User {
  id: number;
  name: string;
}

const IdeasFilter: React.FC<IdeasFilterProps> = ({
  toggleFilter,
  filterIdeas,
}) => {
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const userId = useSelector(
    (state: RootState) => state.user.userDetails?.userId,
  );
  const supportAndPartnershipPreferences = useSelector(
    (state: RootState) => state.support_or_partnership,
  );
  const reduxSchools = useSelector((state: RootState) => state.schools.schools);
  const reduxDepartments = useSelector(
    (state: RootState) => state.departments.departments,
  );

  const [sortType, setSortType] = useState<'asc' | 'desc'>('desc');
  const [following, setFollowing] = useState<boolean | null>(null);
  const [selectedSupports, setSelectedSupports] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedSchools, setSelectedSchools] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedDepartments, setSelectedDepartments] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedDepartmentsBySchool, setSelectedDepartmentsBySchool] =
    useState<{ id: number; name: string }[]>([]);
  const [filterUsers, setFilterUsers] = useState<User[] | null>([]);
  const [selectedUsers, setSelectedUsers] = useState<
    { id: number; name: string }[]
  >([]);

  const parsedSupportsOptions =
    supportAndPartnershipPreferences.support_or_partnership;
  const [departmentsOptionsMessage, setDepartmentsOptionsMessage] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      fetchIdeasCount();
    }
  }, [
    userId,
    following,
    selectedSupports,
    selectedSchools,
    selectedDepartments,
    selectedUsers,
  ]);

  useEffect(() => {
    const fetchFilterUsers = async () => {
      const filterUsersResponse = await getUsersHavingIdeas(
        selectedSchools.length === 1 ? selectedSchools[0].id : null,
        selectedDepartments.length === 1 ? selectedDepartments[0].id : null,
      );

      if ('code' in filterUsersResponse) {
        console.error('Error fetching filter users:', filterUsersResponse);
        if (filterUsersResponse.code !== 'NOT_FOUND')
          setError(filterUsersResponse.message);
        setFilterUsers([]);
      } else {
        setError(null);
        setFilterUsers((filterUsersResponse as FilterUsersResponse).users);
      }
    };

    fetchFilterUsers();
  }, [userId, following, selectedUsers, selectedDepartments, selectedSchools]);

  const updateSortType = (sort_order: 'asc' | 'desc') => {
    setSortType(sort_order);
  };

  const updateFollowing = (value: boolean | null) => {
    setFollowing(value);
  };

  const selectSupportsPartnership = (
    selectedOptions:
      | SingleValue<{ value: number; label: string }>
      | MultiValue<{ value: number; label: string }>,
  ) => {
    setError(null);
    if (Array.isArray(selectedOptions)) {
      const supports = selectedOptions.map((option) => ({
        id: option.value,
        name: option.label,
      }));
      setSelectedSupports(supports);
    } else if (selectedOptions) {
      const singleOption = selectedOptions as SingleValue<{
        value: number;
        label: string;
      }>;
      setSelectedSupports(
        singleOption
          ? [{ id: singleOption.value, name: singleOption.label }]
          : [],
      );
    } else {
      setSelectedSupports([]);
    }
  };

  const selectSchools = (
    selectedOptions:
      | SingleValue<{ value: number; label: string }>
      | MultiValue<{ value: number; label: string }>,
  ) => {
    setError(null);
    if (Array.isArray(selectedOptions)) {
      const schools = selectedOptions.map((option) => ({
        id: option.value,
        name: option.label,
      }));
      setSelectedSchools(schools);
      const departmentsBySchoolsArray: { id: number; name: string }[] = [];
      reduxDepartments.forEach((department) => {
        selectedOptions.forEach((option) => {
          if (department.school_id === option.value) {
            departmentsBySchoolsArray.push({
              id: department.id,
              name: department.name,
            });
          }
        });
      });
      setSelectedDepartmentsBySchool(
        departmentsBySchoolsArray.length ? departmentsBySchoolsArray : [],
      );
      if (departmentsBySchoolsArray.length === 0) {
        console.log('No matching departments found.');
        setSelectedDepartmentsBySchool([]);
        setSelectedDepartments([]);
        setDepartmentsOptionsMessage('No departments available');
      }
    } else if (selectedOptions) {
      const singleOption = selectedOptions as SingleValue<{
        value: number;
        label: string;
      }>;
      setSelectedSchools(
        singleOption
          ? [{ id: singleOption.value, name: singleOption.label }]
          : [],
      );
      const departmentsBySchoolsArray: { id: number; name: string }[] = [];
      if (singleOption !== null) {
        reduxDepartments.forEach((department) => {
          if (department.school_id === singleOption.value) {
            departmentsBySchoolsArray.push({
              id: department.id,
              name: department.name,
            });
          }
        });
        setSelectedDepartmentsBySchool(
          departmentsBySchoolsArray.length ? departmentsBySchoolsArray : [],
        );
        if (departmentsBySchoolsArray.length === 0) {
          console.log('No matching departments found.');
          setSelectedDepartmentsBySchool([]);
          setSelectedDepartments([]);
          setSelectedUsers([]);
          setDepartmentsOptionsMessage(
            'There are no departments available for the selected school. Please select a different school to see available departments.',
          );
        } else {
          setSelectedUsers([]);
          setSelectedDepartmentsBySchool(departmentsBySchoolsArray);
        }
      }
    } else {
      setSelectedSchools([]);
      setSelectedDepartments([]);
      setSelectedDepartmentsBySchool([]);
      setSelectedUsers([]);
      setDepartmentsOptionsMessage(
        'Please select a school first to see available departments.',
      );
    }
  };

  const selectDepartments = (
    selectedOptions:
      | SingleValue<{ value: number; label: string }>
      | MultiValue<{ value: number; label: string }>,
  ) => {
    setError(null);
    if (Array.isArray(selectedOptions)) {
      const departments = selectedOptions.map((option) => ({
        id: option.value,
        name: option.label,
      }));
      setSelectedDepartments(departments);
    } else if (selectedOptions) {
      const singleOption = selectedOptions as SingleValue<{
        value: number;
        label: string;
      }>;
      setSelectedDepartments(
        singleOption
          ? [{ id: singleOption.value, name: singleOption.label }]
          : [],
      );
    } else {
      setSelectedDepartments([]);
      setSelectedUsers([]);
    }
  };

  const selectUsers = (
    selectedOptions:
      | SingleValue<{ value: number; label: string }>
      | MultiValue<{ value: number; label: string }>,
  ) => {
    setError(null);
    if (Array.isArray(selectedOptions)) {
      const users = selectedOptions.map((option) => ({
        id: option.value,
        name: option.label,
      }));
      setSelectedUsers(users);
    } else if (selectedOptions) {
      const singleOption = selectedOptions as SingleValue<{
        value: number;
        label: string;
      }>;
      const user = singleOption
        ? { id: singleOption.value, name: singleOption.label }
        : null;
      setSelectedUsers(user ? [user] : []);
    } else {
      setSelectedUsers([]);
    }
  };

  const fetchIdeasCount = async () => {
    try {
      setError(null);
      setLoading(true);
      if (!userId) throw new Error('User ID is not defined');
      const supportOrPartnership = selectedSupports.map(
        (support) => support.id,
      );
      const schoolIds = selectedSchools.map((school) => school.id);
      const departmentIds = selectedDepartments.map(
        (department) => department.id,
      );

      const data = await getIdeasCount(
        userId,
        following,
        supportOrPartnership,
        schoolIds,
        departmentIds,
        selectedUsers,
      );
      if ('code' in data) {
        if (data.code === 'NOT_FOUND' || data.code === 'BAD_REQUEST') {
          console.error('No ideas found');
          setTotalCount(0);
        } else {
          console.error('Unexpected error code:', data.code);
          setTotalCount(0);
        }
        setLoading(false);
      } else {
        setTotalCount((data as CountResponse).total_count);
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch ideas:', err);
      setTotalCount(0);
      setLoading(false);
    }
  };

  const fetchIdeasFilter = () => {
    const users: number[] = [];
    selectedUsers.map((user) => users.push(user.id));
    filterIdeas({
      userId: userId,
      sort: sortType,
      following,
      support_id: selectedSupports.map((support) => support.id),
      school_id: selectedSchools.map((school) => school.id),
      department_id: selectedDepartments.map((department) => department.id),
      idea_owner_id: users,
    });
  };

  return (
    <div className="filter-container">
      <div className="filter-sub-container grid gap-25 col-lg-4 col-md-6 col-sm-8 col-xs-12">
        <div className="filter-header">
          <Text className="medium-bold">Filter Ideas</Text>
          <Button
            type="button"
            className="small-semi-bold filter-close-button"
            onClick={() => toggleFilter(false)}
          >
            Close
          </Button>
        </div>

        <div className="line" />
        <div className="filter-selections">
          <div className="form-row">
            <Text className="section-data">Sort By</Text>
            <div className="filter-button-group">
              <Button
                type="button"
                onClick={() => updateSortType('desc')}
                className={`role-button ${sortType === 'desc' ? 'selected' : ''}`}
              >
                Most Recent First
              </Button>
              <Button
                type="button"
                onClick={() => updateSortType('asc')}
                className={`role-button ${sortType === 'asc' ? 'selected' : ''}`}
              >
                Most Recent Last
              </Button>
            </div>
          </div>

          <div className="form-row">
            <Text className="section-data">Following</Text>
            <div className="filter-button-group">
              <Button
                type="button"
                onClick={() => updateFollowing(null)}
                className={`role-button ${following === null ? 'selected' : ''}`}
              >
                All
              </Button>
              <Button
                type="button"
                onClick={() => updateFollowing(true)}
                className={`role-button ${following === true ? 'selected' : ''}`}
              >
                Following
              </Button>
              <Button
                type="button"
                onClick={() => updateFollowing(false)}
                className={`role-button ${following === false ? 'selected' : ''}`}
              >
                Not Following
              </Button>
            </div>
          </div>

          <div className="form-row">
            <Label
              htmlFor="support-partnership"
              className="filter-row-header input-label"
            >
              Partnership or Support Needed
              <img
                src={SupportParnershipInfo}
                alt="info-icon"
                className="support-parnership-icon"
              />
            </Label>
            <CustomSelect
              options={parsedSupportsOptions.map((option: Partnership) => ({
                label: option.name,
                value: option.id,
              }))}
              onChange={selectSupportsPartnership}
              value={selectedSupports.map((option) => ({
                label: option.name,
                value: option.id,
              }))}
              placeholder="Select all that apply"
              isMulti={true}
              isClearable={true}
              className="select-support-options"
            />
          </div>

          <div className="form-row">
            <Text className="section-data">
              Principal Investigator’s School
            </Text>
            <CustomSelect
              options={reduxSchools.map((school) => ({
                label: school.name,
                value: school.id,
              }))}
              onChange={selectSchools}
              value={selectedSchools.map((school) => ({
                label: school.name,
                value: school.id,
              }))}
              placeholder="Select School"
              isClearable={true}
              noOptionsMessage="No schools available"
            />
          </div>

          <div className="form-row">
            <Text className="section-data">
              Principal Investigator’s Department
            </Text>
            <CustomSelect
              options={selectedDepartmentsBySchool.map((department) => ({
                label: department.name,
                value: department.id,
              }))}
              onChange={selectDepartments}
              value={selectedDepartments.map((department) => ({
                label: department.name,
                value: department.id,
              }))}
              placeholder="Select Department"
              isClearable={true}
              noOptionsMessage={
                departmentsOptionsMessage !== null
                  ? departmentsOptionsMessage
                  : 'Please select a school first to see available departments.'
              }
            />
          </div>

          <div className="form-row">
            <Text className="section-data">Principal Investigator</Text>
            <CustomSelect
              options={
                filterUsers?.map((user) => ({
                  label: `${user.name}`,
                  value: user.id,
                })) || []
              }
              onChange={selectUsers}
              value={selectedUsers.map((user) => ({
                label: user.name,
                value: user.id,
              }))}
              placeholder="Select User(s) by Name"
              isMulti={false}
              isClearable={true}
              className="select-user-options"
              noOptionsMessage="No users found. Please adjust your search criteria."
              menuPlacement="top"
            />
          </div>
        </div>
        <div className="line" />
        {error && (
          <div className="error-message">
            <div>{error}</div>
          </div>
        )}
        <div className="d-flex justify-content-center">
          <Button
            type="button"
            className={`tertiary-button ${totalCount === 0 ? 'disabled-button' : ''}`}
            onClick={fetchIdeasFilter}
            disabled={totalCount === 0}
          >
            {loading ? (
              <>
                <Loading size={25} /> &nbsp; Show Results
              </>
            ) : (
              'Show ' + totalCount + ' Results'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IdeasFilter;
